import { useSuspendingGet } from '@threatadvisor/react-utils';
import { Client, TeamSetting } from '@threatadvisor/react-utils/src/types/generated.Framework';
import React, { ReactElement, useMemo } from 'react';

export type MdrTeamProps = {
  securityEventApi: string;
  clientUid: string;
  clientName: string;
};

export default function MdrTeam(props: MdrTeamProps): ReactElement {
  const { securityEventApi, clientUid, clientName } = props;

  // this will come from the user in the future and what team they are, hardcoded for now
  const data = useSuspendingGet<TeamSetting[]>(`${securityEventApi}/Settings/team`);
  const socTeam = data[0];

  const clientSettings = useSuspendingGet<Client>(
    `${securityEventApi}/Settings/client/${clientUid && clientUid}`
  );

  const clientContact = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.clientContact,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientContact
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientContact
        )?.phoneNumber,
        title: 'Primary Contact'
      };
  }, [clientSettings]);

  const clientSecondary = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.clientSecondary,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientSecondary
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientSecondary
        )?.phoneNumber,
        title: 'Secondary Contact'
      };
  }, [clientSettings]);

  const clientTertiary = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.clientTertiary,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientTertiary
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.clientTertiary
        )?.phoneNumber,
        title: 'Tertiary Contact'
      };
  }, [clientSettings]);
  const programLead = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.programLead,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.programLead
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.programLead
        )?.phoneNumber,
        title: 'Program Lead'
      };
  }, [clientSettings]);

  const programSecondary = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.programSecondary,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.programSecondary
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.programSecondary
        )?.phoneNumber,
        title: 'Program Secondary'
      };
  }, [clientSettings]);

  const projectCoordinator = useMemo(() => {
    if (clientSettings && clientSettings.users)
      return {
        email: clientSettings.caseManagementSettings.projectCoordinator,
        fullName: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.projectCoordinator
        )?.fullName,
        phoneNumber: clientSettings.users.find(
          usr => usr.email === clientSettings.caseManagementSettings.projectCoordinator
        )?.phoneNumber,
        title: 'Project Coordinator'
      };
  }, [clientSettings]);

  const teamEmail =
    clientSettings.mailboxes && clientSettings.mailboxes.length > 0
      ? clientSettings.mailboxes[0].mailbox
      : '';

  return (
    <>
      <a id="hTeamInfo"></a>
      <h2 style={{ marginTop: 0 }} className="text-uppercase">
        {clientName} MDR Program Support Team
      </h2>
      <hr />

      <h4 className="text-uppercase">DirectDefense SOC</h4>
      <p style={{ marginLeft: '2em' }}>
        {socTeam.analystHeadCount + socTeam.projectSupportHeadCount + socTeam.seniorStaffHeadCount}{' '}
        DirectDefense personnel supporting {clientName} MDR Program
      </p>
      <ul style={{ marginLeft: '2em' }}>
        <li>{socTeam.seniorStaffHeadCount} Senior Staff</li>
        <li>{socTeam.analystHeadCount} Security Analysts</li>
        <li>{socTeam.projectSupportHeadCount} Project Managers</li>
      </ul>
      <hr />

      <h4 className="text-uppercase">Direct Defense Contacts</h4>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>SOC</li>
        <li>Email: {teamEmail}</li>
        <li>Phone: 800-675-3367</li>
      </ul>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Primary Program Lead</li>
        <li>Name: {programLead?.fullName}</li>
        <li>Email: {programLead?.email}</li>
        <li>Phone: {programLead?.phoneNumber}</li>
      </ul>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Secondary Program Lead</li>
        <li>Name: {programSecondary?.fullName}</li>
        <li>Email: {programSecondary?.email}</li>
        <li>Phone: {programSecondary?.phoneNumber}</li>
      </ul>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Project Coordinator</li>
        <li>Name: {projectCoordinator?.fullName}</li>
        <li>Email: {projectCoordinator?.email}</li>
        <li>Phone: {projectCoordinator?.phoneNumber}</li>
      </ul>

      <hr />

      <h4 className="text-uppercase">Client Contacts</h4>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Primary Contact</li>
        <li>Name: {clientContact?.fullName}</li>
        <li>Email: {clientContact?.email}</li>
        <li>Phone: {clientContact?.phoneNumber}</li>
      </ul>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Secondary Contact</li>
        <li>Name: {clientSecondary?.fullName}</li>
        <li>Email: {clientSecondary?.email}</li>
        <li>Phone: {clientSecondary?.phoneNumber}</li>
      </ul>
      <ul className="list-style-none" style={{ marginLeft: '2em' }}>
        <li style={{ fontWeight: 'bolder' }}>Tertiary Contact</li>
        <li>Name: {clientTertiary?.fullName}</li>
        <li>Email: {clientTertiary?.email}</li>
        <li>Phone: {clientTertiary?.phoneNumber}</li>
      </ul>
    </>
  );
}
